/**
 * Media Sync Transform Functions
 *
 * Converts API response data (MediaFileDto[]) to client models (Campaign[], PlaylistItem[])
 * used by SchedulerStore and MediaStore.
 */

import {
  MediaFileDto,
  PlayerGroupScheduleDto,
  PlayerScheduleDto,
  PlaylistScheduleDto,
  SlotDto,
  SlotSequenceDto,
  SyncResponse,
  WeatherConditionDto,
} from '~/api/types/sync';
import { resolveApiBaseUrl } from '~/services/embedded/hostConfig';
import {
  Campaign,
  PlayerGroupSchedule,
  PlayerSchedule,
  PlaylistItem,
  PlaylistSchedule,
  TriggerDefinition,
  TriggerMode,
  WeatherConditionRule,
} from '~/services/scheduler/types';
import type {
  PlaylistExecutionType,
  Slot,
  SlotContentType,
  SlotSequence,
} from '~/services/playback/types';

/**
 * R5 (2026-06-20) — Lê a flag de campanha de emergência de um MediaFileDto.
 * Server envia em camelCase (`isEmergencyFallback`); aceitamos a variante snake
 * (`is_emergency_fallback`) por robustez. Vide regras-temporais-player.md R5.
 */
function isEmergencyFallbackFile(file: MediaFileDto): boolean {
  return (
    file.isEmergencyFallback === true || file.is_emergency_fallback === true
  );
}

/** Normaliza a condição de clima (snake do sync → camel). null = sem regra. */
export function normWeatherCondition(
  raw?: WeatherConditionDto | null,
): WeatherConditionRule | null {
  if (!raw) return null;
  const num = (
    r?: { op: string; value?: number | null; value2?: number | null } | null,
  ) =>
    r ? { op: r.op, value: r.value ?? null, value2: r.value2 ?? null } : null;
  return {
    conditions: raw.conditions ?? null,
    temp: num(raw.temp),
    feelsLike: num(raw.feels_like),
    humidity: num(raw.humidity),
    wind: num(raw.wind),
    clouds: num(raw.clouds),
    rainMm: num(raw.rain_mm),
  };
}

// =============================================================================
// MAIN TRANSFORM FUNCTION
// =============================================================================

export interface TransformResult {
  campaigns: Campaign[];
  allPlaylistItems: PlaylistItem[];
  triggers: TriggerDefinition[];
  /**
   * V060 — Janelas dos grupos a que o player pertence. Cliente usa em
   * `groupScheduleFilter.isGroupSchedulePlayable` pra compor o filtro AND
   * (Conteúdo ∩ PlaylistItem ∩ Campanha ∩ Grupo). Vazio quando server não
   * envia (player sem grupo ou grupos sem schedule).
   */
  playerGroups: PlayerGroupSchedule[];
  /**
   * V066 — Janela do próprio player. 5ª camada AND com early return.
   * Single object (player tem 1 schedule próprio). Sem bypass — janela
   * aplica a TUDO no cliente, inclusive associação direta.
   *
   * NULL = sem schedule (player liberado 24/7 — back-compat).
   */
  playerSchedule: PlayerSchedule | null;
  /**
   * V067 — Janelas das playlists de origem dos itens. Cliente usa em
   * `playlistScheduleFilter.isPlaylistSchedulePlayable` pra compor o filtro
   * AND (6ª camada). UNIÃO via `viaPlaylists`; itens sem `viaPlaylists`
   * (anexas) fazem BYPASS. Vazio quando server não envia.
   */
  playlistSchedules: PlaylistSchedule[];
}

/**
 * V060 — Normaliza PlayerGroupScheduleDto (snake do sync) pra
 * PlayerGroupSchedule (camel) consumido pelo scheduler/store.
 */
function transformPlayerGroup(g: PlayerGroupScheduleDto): PlayerGroupSchedule {
  return {
    id: g.id,
    name: g.nome ?? undefined,
    validFrom: g.valid_from ?? null,
    validUntil: g.valid_until ?? null,
    startAt: g.start_at ?? null,
    endAt: g.end_at ?? null,
    daysOfWeek: g.days_of_week ?? undefined,
    scheduleMode: g.schedule_mode ?? undefined,
    weeklySchedule: g.weekly_schedule ?? undefined,
  };
}

/**
 * V066 — Normaliza PlayerScheduleDto (snake do sync) pra PlayerSchedule
 * (camel) consumido pelo scheduler/store. NULL quando server não envia.
 */
function transformPlayerSchedule(
  p: PlayerScheduleDto | null | undefined,
): PlayerSchedule | null {
  if (!p) return null;
  return {
    id: p.id,
    name: p.nome ?? undefined,
    validFrom: p.valid_from ?? null,
    validUntil: p.valid_until ?? null,
    startAt: p.start_at ?? null,
    endAt: p.end_at ?? null,
    daysOfWeek: p.days_of_week ?? undefined,
    scheduleMode: p.schedule_mode ?? undefined,
    weeklySchedule: p.weekly_schedule ?? undefined,
  };
}

/**
 * V067 — Normaliza PlaylistScheduleDto (snake do sync) pra PlaylistSchedule
 * (camel) consumido pelo scheduler/store. Sem `scheduleMode` (inferido no
 * cliente a partir de `weeklySchedule`).
 */
function transformPlaylist(p: PlaylistScheduleDto): PlaylistSchedule {
  return {
    id: p.id,
    name: p.nome ?? undefined,
    validFrom: p.valid_from ?? null,
    validUntil: p.valid_until ?? null,
    startAt: p.start_at ?? null,
    endAt: p.end_at ?? null,
    daysOfWeek: p.days_of_week ?? undefined,
    weeklySchedule: p.weekly_schedule ?? undefined,
  };
}

/**
 * Transforms sync response into client models.
 *
 * Creates a single campaign with all active files.
 * The server already filters by validity, so all returned files should be displayed.
 */
export function transformSyncResponse(response: SyncResponse): TransformResult {
  const { files, sync_timestamp } = response;

  // V060 — normaliza grupos do player sempre, mesmo em early-return.
  const playerGroups: PlayerGroupSchedule[] = (
    response.player_groups ?? []
  ).map(transformPlayerGroup);

  // V066 — normaliza schedule do próprio player. NULL = sem schedule (24/7).
  const playerSchedule: PlayerSchedule | null = transformPlayerSchedule(
    response.player_schedule,
  );

  // V067 — normaliza janelas das playlists de origem sempre, mesmo em
  // early-return (schedule de playlist pode mudar sem mexer no library_hash).
  const playlistSchedules: PlaylistSchedule[] = (response.playlists ?? []).map(
    transformPlaylist,
  );

  if (!files || files.length === 0) {
    return {
      campaigns: [],
      allPlaylistItems: [],
      triggers: [],
      playerGroups,
      playerSchedule,
      playlistSchedules,
    };
  }

  // ============================================================================
  // Step 1: Separate campaign files from trigger files
  // ============================================================================

  const campaignFiles: MediaFileDto[] = [];
  const triggerFiles: MediaFileDto[] = [];
  const contentTriggerFiles: MediaFileDto[] = [];

  files.forEach((file) => {
    if (file.tipo === 'GATILHO') {
      triggerFiles.push(file);
    } else if (file.tipo === 'CONTENT_TRIGGER') {
      // "Tocar um Conteúdo" (F1/F2) — conteúdo avulso disparável por um botão
      // (action ref_kind='content'). NÃO entra no loop de campanha; vira uma
      // TriggerDefinition standalone chaveada pelo id do CONTEÚDO (UUID) no
      // Step 3. Vide FEATURE-BOTAO-TOCAR-CONTEUDO.md.
      contentTriggerFiles.push(file);
    } else {
      campaignFiles.push(file);
    }
  });

  // ============================================================================
  // Step 2: Group CAMPAIGN files by campanha_id
  // ============================================================================

  // 1. Agrupar arquivos por campanha_id
  const filesByCampanhaId = new Map<number | string, MediaFileDto[]>();
  const filesWithoutCampanha: MediaFileDto[] = [];

  campaignFiles.forEach((file) => {
    const campanhaId = file.campanha_id;
    if (campanhaId != null && campanhaId !== undefined) {
      const key =
        typeof campanhaId === 'number' ? campanhaId : String(campanhaId);
      if (!filesByCampanhaId.has(key)) {
        filesByCampanhaId.set(key, []);
      }
      filesByCampanhaId.get(key)!.push(file);
    } else {
      // Arquivos sem campanha_id vão para uma campanha especial
      filesWithoutCampanha.push(file);
    }
  });

  // 2. Para cada campanha_id, criar uma campanha real
  const campaigns: Campaign[] = [];
  const allPlaylistItems: PlaylistItem[] = [];

  // Processar campanhas com ID real
  filesByCampanhaId.forEach((campaignFiles, campanhaIdKey) => {
    // Ordenar arquivos por prioridade dentro da campanha
    // Prioridade MENOR = passa PRIMEIRO (0 = máxima prioridade)
    const sortedFiles = [...campaignFiles].sort((a, b) => {
      const prioA = a.priority ?? 0;
      const prioB = b.priority ?? 0;

      // Mesma prioridade = randomizar
      if (prioA === prioB) {
        return Math.random() - 0.5;
      }

      // Prioridade menor = passa primeiro (ordem crescente)
      return prioA - prioB;
    });

    // Transformar em PlaylistItems
    const playlistItems = sortedFiles.map(transformMediaFile);
    allPlaylistItems.push(...playlistItems);

    // Criar campanha com ID real (não sintético)
    const campaignId =
      typeof campanhaIdKey === 'number' ? String(campanhaIdKey) : campanhaIdKey;

    // Usar a menor prioridade dos arquivos como prioridade da campanha
    // (0 = máxima prioridade, então menor número = maior prioridade)
    const campaignPriority = Math.min(
      ...campaignFiles.map((f) => f.priority ?? 0),
    );

    const campaign: Campaign = {
      id: campaignId, // ✅ ID real (1, 2, 3, etc.) em vez de sync-campaign-...
      name: `Campanha ${campaignId}`, // Nome temporário (pode ser melhorado depois)
      description: `Campanha com ${playlistItems.length} itens`,
      startDate: '1970-01-01',
      endDate: '2099-12-31',
      priority: campaignPriority, // Prioridade da campanha (0 = máxima)
      // Fase 2.0 — condição de clima da CAMPANHA (mesma p/ todos os files).
      // V088 (2026-08-06): server NOVO manda `weather_condition_campaign`; o
      // campo legado `weather_condition` continua sendo enviado (espelhado)
      // pra back-compat com players antigos. Aqui lemos o novo primeiro e
      // caímos no legado se não vier — cobre server antigo E ordem de deploy
      // server-primeiro. A regra do CONTEÚDO fica em PlaylistItem
      // (weatherConditionContent, AND separado em contentScheduleFilter).
      weatherCondition: normWeatherCondition(
        campaignFiles[0]?.weather_condition_campaign ??
          campaignFiles[0]?.weather_condition,
      ),
      // R5 (2026-06-20) — campanha de emergência do tenant. Marcada quando
      // QUALQUER file da campanha carrega a flag (server marca todos os files
      // da programação de emergência). Player usa como last-resort 24/7.
      isEmergencyFallback: campaignFiles.some(isEmergencyFallbackFile),
      schedule: [
        {
          id: `schedule-${campaignId}`,
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          startTime: '00:00',
          endTime: '23:59',
          timezone: 'America/Sao_Paulo',
        },
      ],
      playlist: playlistItems,
      status: 'active',
      createdAt: sync_timestamp,
      updatedAt: sync_timestamp,
    };

    campaigns.push(campaign);
  });

  // 3. Processar arquivos sem campanha_id (criar campanha especial "sem-campanha")
  if (filesWithoutCampanha.length > 0) {
    const sortedFiles = [...filesWithoutCampanha].sort((a, b) => {
      const prioA = a.priority ?? 0;
      const prioB = b.priority ?? 0;
      return prioB - prioA;
    });

    const playlistItems = sortedFiles.map(transformMediaFile);
    allPlaylistItems.push(...playlistItems);

    const campaign: Campaign = {
      id: 'sem-campanha', // ID especial para arquivos sem campanha
      name: 'Conteúdo sem Campanha',
      description: `Conteúdo sem campanha associada (${playlistItems.length} itens)`,
      startDate: '1970-01-01',
      endDate: '2099-12-31',
      priority: 0, // Menor prioridade
      schedule: [
        {
          id: 'schedule-sem-campanha',
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          startTime: '00:00',
          endTime: '23:59',
          timezone: 'America/Sao_Paulo',
        },
      ],
      playlist: playlistItems,
      status: 'active',
      createdAt: sync_timestamp,
      updatedAt: sync_timestamp,
    };

    campaigns.push(campaign);
  }

  // ============================================================================
  // Step 3: Build TriggerDefinitions from trigger files
  // ============================================================================

  const triggersByCampanhaId = new Map<number | string, MediaFileDto[]>();
  triggerFiles.forEach((file) => {
    const key = file.campanha_id ?? 'unknown-trigger';
    if (!triggersByCampanhaId.has(key)) {
      triggersByCampanhaId.set(key, []);
    }
    triggersByCampanhaId.get(key)!.push(file);
  });

  const triggers: TriggerDefinition[] = [];
  triggersByCampanhaId.forEach((tFiles, triggerIdKey) => {
    const sortedFiles = [...tFiles].sort(
      (a, b) => (a.priority ?? 0) - (b.priority ?? 0),
    );
    const playlistItems = sortedFiles.map(transformMediaFile);
    // Include trigger items in allPlaylistItems for caching
    allPlaylistItems.push(...playlistItems);

    const defaultMode: TriggerMode =
      (tFiles[0]?.trigger_mode as TriggerMode) || 'ONCE';

    triggers.push({
      id: String(triggerIdKey),
      name: tFiles[0]?.name || `Trigger ${triggerIdKey}`,
      defaultMode,
      playlist: playlistItems,
    });
  });

  // "Tocar um Conteúdo" — cada CONTENT_TRIGGER vira uma TriggerDefinition
  // standalone (playlist de 1 item), chaveada pelo id do CONTEÚDO (UUID). O
  // botão (action ref_kind='content') dispara via content_id →
  // triggerDefinitions.get(uuid). Reusa o MESMO mapa dos gatilhos — UUID não
  // colide com id numérico de campanha/gatilho. Modo ONCE (toca 1× e volta).
  contentTriggerFiles.forEach((file) => {
    if (!file.id) {
      return;
    }
    const item = transformMediaFile(file);
    allPlaylistItems.push(item); // pré-cache local (offline-first)
    triggers.push({
      id: String(file.id),
      name: file.name || `Conteúdo ${file.id}`,
      defaultMode: 'ONCE',
      playlist: [item],
    });
  });

  const result = {
    campaigns,
    allPlaylistItems,
    triggers,
    playerGroups,
    playerSchedule,
    playlistSchedules,
  };

  return result;
}

// =============================================================================
// INDIVIDUAL TRANSFORMS
// =============================================================================

/**
 * Transforms a MediaFileDto to a PlaylistItem.
 */
function transformMediaFile(file: MediaFileDto): PlaylistItem {
  const resolvedUrl = resolveMediaUrl(file.url);
  return {
    id: file.id || file.url || file.checksum || 'unknown-media',
    type:
      file.type === 'stream'
        ? 'stream'
        : file.type === 'video'
          ? 'video'
          : 'image',
    name: file.name,
    url: resolvedUrl,
    duration: file.duration_seconds || 10, // 10 seconds default for images
    hasDurationOverride: file.has_duration_override === true,
    fileSize: file.size_bytes,
    checksum: normalizeChecksum(file.checksum),
    mimeType: file.mime_type,
    startAt: file.start_at ?? undefined,
    endAt: file.end_at ?? undefined,
    daysOfWeek: file.days_of_week ?? undefined,
    // V059 — schedule mode + weekly schedule do CONTEÚDO (não da campanha).
    // Player filtra hora-do-dia em contentScheduleFilter.ts.
    scheduleMode: file.schedule_mode ?? undefined,
    weeklySchedule: file.weekly_schedule ?? undefined,
    // 2026-06-12 — Validade (date-only, "YYYY-MM-DD"). Filtrada por
    // isContentValidNow no fuso LOCAL do device (vide R0 em
    // regras-temporais-player.md).
    validFrom: file.valid_from ?? null,
    validUntil: file.valid_until ?? null,
    campanhaId: file.campanha_id ?? undefined,
    playbackStartedAt: file.playback_started_at ?? undefined,
    elementos: file.elements_enabled !== false ? file.elementos : undefined,
    // V060 — origem da associação (direto vs grupo) pra player decidir
    // bypass (viaDireto) ou UNIÃO de janelas (viaGrupos). Player filtra
    // em groupScheduleFilter.ts.
    viaDireto: file.via_direto === true ? true : undefined,
    viaGrupos:
      file.via_grupos && file.via_grupos.length > 0
        ? [...file.via_grupos]
        : undefined,
    // V067 — playlists de origem pra player decidir UNIÃO de janelas
    // (viaPlaylists). Vazio/ausente = anexa → BYPASS. Player filtra em
    // playlistScheduleFilter.ts.
    viaPlaylists:
      file.via_playlists && file.via_playlists.length > 0
        ? [...file.via_playlists]
        : undefined,
    // R5 — item da emergencial. Não muda reprodução (o scheduler já a mantém
    // fora do loop); serve pro prefetch despriorizar o download. Vide
    // `prefetchAll` em media.store.
    isEmergencyFallback: isEmergencyFallbackFile(file) ? true : undefined,
    // Multi-zone — zona_id da associação de conteúdo no server.
    // Undefined quando não há zonas (layout single-viewport legado).
    zonaId: file.zona_id != null ? file.zona_id : undefined,
    streamUrl: file.stream_url ?? undefined,
    startOffsetSeconds: file.start_offset_seconds ?? undefined,
    // V088 (2026-08-06) — regra de clima do CONTEÚDO (AND com a da campanha).
    // Só o campo `weather_condition_content` é do conteúdo — não fazemos fallback
    // pro campo legado `weather_condition` (esse é da CAMPANHA).
    weatherConditionContent: normWeatherCondition(
      file.weather_condition_content,
    ),
  };
}

function resolveMediaUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    const base = resolveApiBaseUrl();
    return `${base}${url}`;
  }
  return url;
}

/**
 * Removes "sha256:" prefix from checksum if present.
 * Returns empty string if checksum is null/undefined.
 */
function normalizeChecksum(checksum: string | null): string {
  if (!checksum) {
    return '';
  }
  if (checksum.startsWith('sha256:')) {
    return checksum.substring(7);
  }
  return checksum;
}

// =============================================================================
// Sprint 2 CP7 — SlotSequence transform (snake_case → camelCase)
// =============================================================================

const VALID_EXECUTION_TYPES: PlaylistExecutionType[] = [
  'SEQUENTIAL',
  'RANDOM',
  'WEIGHTED_RANDOM',
  'FREQUENCY_CONTROLLED',
];

/**
 * Sprint 2 CP7 — normaliza {@link SlotSequenceDto} (snake_case do backend)
 * pra {@link SlotSequence} (camelCase do client). Aplica `resolveMediaUrl`
 * em cada `content_url` reusando o mesmo helper de {@link transformMediaFile}.
 *
 * Retorna {@code null} quando entrada é null/undefined ou inválida —
 * caller (playback.store) trata como "sem slot_sequence" e cai no fluxo
 * legado de files[].
 */
export function transformSlotSequence(
  dto: SlotSequenceDto | null | undefined,
): SlotSequence | null {
  if (!dto) return null;
  if (!Array.isArray(dto.slots)) return null;

  // Defensive: validate execution_type — backend pode adicionar novos enums
  // no futuro. Se vier algo desconhecido, usa SEQUENTIAL como fallback safe
  // (player vai tocar na ordem).
  const executionType = (VALID_EXECUTION_TYPES as string[]).includes(
    dto.execution_type,
  )
    ? (dto.execution_type as PlaylistExecutionType)
    : 'SEQUENTIAL';

  return {
    playlistId: dto.playlist_id,
    playlistName: dto.playlist_name,
    executionType,
    tier: dto.tier,
    hourWindowStart: dto.hour_window_start,
    totalDurationS: dto.total_duration_s,
    slots: dto.slots.map(transformSlot),
  };
}

function transformSlot(dto: SlotDto): Slot {
  return {
    sequence: dto.sequence,
    scheduledOffsetS: dto.scheduled_offset_s,
    durationS: dto.duration_s,
    hasDurationOverride: dto.has_duration_override === true,
    contentId: dto.content_id,
    contentName: dto.content_name,
    contentType: (dto.content_type === 'image'
      ? 'image'
      : dto.content_type === 'stream'
        ? 'stream'
        : 'video') as SlotContentType,
    contentUrl: resolveMediaUrl(dto.content_url),
    // Stream (Tier 2/3): a URL vive em stream_url (content_url = null). Sem este
    // mapeamento, currentMedia.streamUrl ficava undefined → C1 falso → AvplayVideo(url=null)
    // → freeze na TV (o guard do stream nem chega a rodar). Espelha o Tier 4 (transformMediaFile).
    streamUrl: dto.stream_url ?? undefined,
    programacaoId: dto.programacao_id,
    playlistCampanhaPriority: dto.playlist_campanha_priority,
    zonaId: dto.zona_id ?? undefined,
    // V088 (2026-08-08) — regra de clima + validade + dia/hora do CONTEÚDO
    // propagadas pra Tier 2/3 (paridade com Tier 4/MediaFileDto). Sem esses
    // campos, o cliente não conseguia aplicar filterPlaylistBySchedule/weather
    // AND no caminho Tier 2/3. Regras-temporais-player.md R0/R2/R4.
    weatherConditionContent: normWeatherCondition(
      dto.weather_condition_content,
    ),
    validFrom: dto.valid_from ?? undefined,
    validUntil: dto.valid_until ?? undefined,
    startAt: dto.start_at ?? undefined,
    endAt: dto.end_at ?? undefined,
    daysOfWeek: dto.days_of_week ?? undefined,
    scheduleMode: dto.schedule_mode ?? undefined,
    weeklySchedule: dto.weekly_schedule ?? undefined,
  };
}

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

export { transformMediaFile, normalizeChecksum };
